"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.black76 = exports.black76Price = exports.calculateShortExpiryTheta = void 0;
const statisticsUtils_1 = require("../calculationsCore/statisticsUtils");
const types_1 = require("../types");
/**
 *
 * Theta value is bound to the contract price. We cannot lose in one day more than what the contract is worth.
 * Since theta is defined as the money decay per day, the last day we can only lose the contract price of the day.
 *
 * @param contractValue
 * @param thetaValue
 */
const calculateShortExpiryTheta = (contractValue, thetaValue) => {
    const sign = Math.sign(thetaValue);
    return contractValue < Math.abs(thetaValue)
        ? sign * contractValue
        : thetaValue;
};
exports.calculateShortExpiryTheta = calculateShortExpiryTheta;
const black76Price = (optionType, F, K, volatility, T, r) => {
    const sqrtT = Math.sqrt(T);
    const ert = Math.exp(-r * T);
    const d1 = (Math.log(F / K) + ((volatility * volatility) / 2) * T) /
        (volatility * sqrtT);
    const d2 = d1 - volatility * sqrtT;
    const cnd_d1 = (0, statisticsUtils_1.CND)(d1);
    const cnd_d2 = (0, statisticsUtils_1.CND)(d2);
    const cnd_negative_d1 = (0, statisticsUtils_1.CND)(-d1);
    const cnd_negative_d2 = (0, statisticsUtils_1.CND)(-d2);
    return optionType === types_1.OptionType.CALL
        ? ert * (F * cnd_d1 - K * cnd_d2)
        : ert * (K * cnd_negative_d2 - F * cnd_negative_d1);
};
exports.black76Price = black76Price;
/**
 *
 * @param F  * Black 76 pricing model.
 * @param F - The future price
 * @param K - Strike
 * @param volatility - Volatility (expressed in %)
 * @param T - Time to Maturity (expressed in Years)
 * @param r - Interest rate
 * @returns Price for Put and Call as well as their greeks.
 */
const black76 = (F, K, volatility, T, r) => {
    const sqrtT = Math.sqrt(T);
    const ert = Math.exp(-r * T);
    const d1 = (Math.log(F / K) + ((volatility * volatility) / 2) * T) /
        (volatility * sqrtT);
    const d2 = d1 - volatility * sqrtT;
    const cnd_d1 = (0, statisticsUtils_1.CND)(d1);
    const cnd_d2 = (0, statisticsUtils_1.CND)(d2);
    const cnd_negative_d1 = (0, statisticsUtils_1.CND)(-d1);
    const cnd_negative_d2 = (0, statisticsUtils_1.CND)(-d2);
    const pdf_d1 = statisticsUtils_1.NormalDist.pdf(d1);
    const pdf_d2 = statisticsUtils_1.NormalDist.pdf(d2);
    const pdf_negative_d1 = statisticsUtils_1.NormalDist.pdf(-d1);
    const pdf_negative_d2 = statisticsUtils_1.NormalDist.pdf(-d2);
    const C = ert * (F * cnd_d1 - K * cnd_d2);
    const P = ert * (K * cnd_negative_d2 - F * cnd_negative_d1);
    const gamma = (ert * pdf_d1) / (F * sqrtT * volatility);
    const vega = (F * ert * pdf_d1 * sqrtT) / 100;
    const deltaCall = ert * cnd_d1;
    const deltaPut = -ert * cnd_negative_d1;
    const rhoCall = -T * ert * (F * pdf_d1 - K * pdf_d2);
    const rhoPut = -T * ert * (K * pdf_negative_d2 - F * pdf_negative_d1);
    const thetaFirstTerm = (-F * ert * (0, statisticsUtils_1.phi)(d1) * volatility) / (2 * sqrtT);
    const rKert = r * K * ert;
    const rFert = r * F * ert;
    let thetaCall = (thetaFirstTerm - rKert * cnd_d2 + rFert * cnd_d1) / 365;
    let thetaPut = (thetaFirstTerm + rKert * cnd_negative_d2 - rFert * cnd_negative_d1) / 365;
    thetaCall = (0, exports.calculateShortExpiryTheta)(C, thetaCall);
    thetaPut = (0, exports.calculateShortExpiryTheta)(P, thetaPut);
    const theta = -Math.min(Math.abs(thetaCall), Math.abs(thetaPut));
    return {
        C,
        P,
        deltaCall,
        deltaPut,
        gamma,
        vega,
        rhoPut,
        rhoCall,
        thetaCall: theta,
        thetaPut: theta,
    };
};
exports.black76 = black76;
